import { ObjectCollection, ObjectQuery } from '@fullstory/renderer-shared';
import { isEqual, throttle } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { RendererClient } from './internal';

const emptyCollection: ObjectCollection = Object.freeze({});

export type ObservationOptions = {
  /** If set, throttle observations to no more often than the given frequency. */
  throttleMs?: number;
};

/**
 * A hook that observes a collection of rendered objects matching the given query.
 * @param rendererClient The client to use for observing objects.
 * @param query The query that defines the objects to observe.
 * @param options Options to control the observation behavior.
 */
export const useRenderedObjectCollection = (
  rendererClient: RendererClient | undefined,
  query: ObjectQuery,
  options: ObservationOptions = {
    throttleMs: 33,
  },
): ObjectCollection => {
  const [objects, _setObjects] = useState<ObjectCollection>(emptyCollection);
  const setObjectsIfChanged = useCallback((newObjects: ObjectCollection) => {
    // when possible, let's keep the existing objects identity the same for stability as this callback is hit frequently
    _setObjects(existingObjects => {
      const existingEntries = Object.entries(existingObjects);
      const newKeys = Object.keys(newObjects);
      if (existingEntries.length != newKeys.length) {
        return newObjects;
      }
      for (const [key, existingObj] of existingEntries) {
        const newObj = newObjects[key];
        if (!newObj) {
          return newObjects;
        }
        // because DOMRect objects can't be compared with isEqual, we need to compare them separately
        const { rect: newObjRect, ...newObjForEqual } = newObj;
        const { rect: existingObjRect, ...existingObjForEqual } = existingObj;
        // compare them first as they are cheaper to compare and are more likely to change
        if (
          !(
            newObjRect.x === existingObjRect.x &&
            newObjRect.y === existingObjRect.y &&
            newObjRect.width === existingObjRect.width &&
            newObjRect.height === existingObjRect.height
          )
        ) {
          return newObjects;
        }
        // then compare the rest
        if (!isEqual(newObjForEqual, existingObjForEqual)) {
          return newObjects;
        }
      }
      return existingObjects;
    });
  }, []);
  const { throttleMs } = options;

  useEffect(() => {
    if (!rendererClient || query.patterns.length === 0) {
      setObjectsIfChanged(emptyCollection);
      return;
    }

    const onObservation = Number.isFinite(throttleMs)
      ? throttle(setObjectsIfChanged, throttleMs, { leading: true, trailing: true })
      : setObjectsIfChanged;

    return rendererClient.addObjectObserver(query, onObservation);
  }, [query, rendererClient, throttleMs]);

  return objects;
};
